import { useEffect } from 'react';

/**
 * Conecta un <input> con el autocompletado de direcciones de Google Places.
 *
 * Los formularios antiguos (FormGastos, FormPrima, FormApud) repiten este mismo bloque
 * inline y revientan si el script de Google no ha cargado. Aquí está aislado y protegido.
 *
 * @param {object} inputRef ref al input de dirección
 * @param {function} onResolved callback con { direccion, provincia, codigo_postal, localidad }
 * @param {array} deps dependencias extra para re-enganchar el listener (p.ej. el paso del formulario)
 */
export function useAddressAutocomplete(inputRef, onResolved, deps = []) {
    useEffect(() => {
        if (!inputRef.current) return;
        if (!window.google?.maps?.places) {
            console.warn('Google Places no está disponible: el autocompletado de dirección queda desactivado');
            return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
        autocomplete.setComponentRestrictions({ country: ['es'] });
        autocomplete.setFields(['address_component']);

        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place?.address_components) return;

            const get = (type) => place.address_components.find(c => c.types.includes(type))?.long_name || '';

            const localidad = get('locality');
            const pais = get('country');
            const provincia = get('administrative_area_level_2');
            const codigoPostal = get('postal_code');

            // La calle viene concatenada con localidad y país: nos quedamos sólo con la calle.
            let direccion = inputRef.current.value;
            [localidad, pais].forEach((parte) => {
                if (!parte) return;
                const index = direccion.indexOf(parte);
                if (index >= 0) direccion = direccion.substr(0, index);
            });
            direccion = direccion.replace(/,\s*$/, '').trim();

            inputRef.current.value = direccion;
            onResolved({ direccion, provincia, codigo_postal: codigoPostal, localidad });
        });

        return () => listener?.remove?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
